import { energiaDeMassa } from './cargaSolta'

export const GRAVIDADE_M_S2 = 9.81

export interface EntradaImpacto {
  massaKg: number
  velocidadeKmh: number
  distanciaParadaM: number
}

export interface ResultadoImpacto {
  entrada: EntradaImpacto
  velocidadeMs: number
  energiaJ: number
  desaceleracaoMs2: number
  desaceleracaoG: number
  tempoParadaS: number
}

function validarValor(valor: number, minimo: number, maximo: number, nome: string) {
  if (!Number.isFinite(valor) || valor < minimo || valor > maximo) {
    throw new RangeError(`${nome} deve estar entre ${minimo} e ${maximo}.`)
  }
}

/** Experimento 1D: um corpo a velocidade vi para em d, com aceleração constante. */
export function calcularImpacto(entrada: EntradaImpacto): ResultadoImpacto {
  validarValor(entrada.massaKg, 1, 10000, 'Massa (kg)')
  validarValor(entrada.velocidadeKmh, 0, 150, 'Velocidade (km/h)')
  validarValor(entrada.distanciaParadaM, DISTANCIA_MIN_M, DISTANCIA_MAX_M, 'Distância (m)')
  const velocidadeMs = entrada.velocidadeKmh / 3.6
  const desaceleracaoMs2 = velocidadeMs ** 2 / (2 * entrada.distanciaParadaM)
  return {
    entrada,
    velocidadeMs,
    energiaJ: energiaDeMassa(entrada.massaKg, entrada.velocidadeKmh).joules,
    desaceleracaoMs2,
    desaceleracaoG: desaceleracaoMs2 / GRAVIDADE_M_S2,
    tempoParadaS: velocidadeMs === 0 ? 0 : velocidadeMs / desaceleracaoMs2,
  }
}

/** Modo por tempo: a distância é derivada, nunca um terceiro controle independente. */
export function distanciaParaTempo(velocidadeKmh: number, tempoParadaS: number): number {
  validarValor(velocidadeKmh, 0, 150, 'Velocidade (km/h)')
  validarValor(tempoParadaS, TEMPO_MIN_S, TEMPO_MAX_S, 'Tempo (s)')
  return velocidadeKmh / 3.6 * tempoParadaS / 2
}

export interface AmostraImpacto {
  tempoS: number
  distanciaM: number
  velocidadeMs: number
  desaceleracaoMs2: number
}

/** A mesma trajetória alimenta visualização, valores e gráficos. */
export function amostrarImpacto(resultado: ResultadoImpacto, progresso: number): AmostraImpacto {
  const fracao = Math.min(1, Math.max(0, Number.isFinite(progresso) ? progresso : 0))
  const t = fracao * resultado.tempoParadaS
  const a = resultado.desaceleracaoMs2
  return {
    tempoS: t,
    distanciaM: fracao === 1 && resultado.velocidadeMs > 0
      ? resultado.entrada.distanciaParadaM
      : resultado.velocidadeMs * t - a * t * t / 2,
    velocidadeMs: fracao === 1 ? 0 : Math.max(0, resultado.velocidadeMs - a * t),
    desaceleracaoMs2: fracao < 1 && resultado.tempoParadaS > 0 ? a : 0,
  }
}

/** Faixa didática da distância de parada aceita pelo modelo. */
export const DISTANCIA_MIN_M = 0.05
export const DISTANCIA_MAX_M = 5
/** Faixa do controle de tempo, antes de ajustar pela velocidade. */
export const TEMPO_MIN_S = 0.02
export const TEMPO_MAX_S = 0.5

/**
 * Tempos de parada possíveis para a velocidade dada: a distância derivada
 * (d = v·t/2) precisa ficar dentro da faixa do modelo. Em repouso, qualquer tempo serve.
 */
export function limitesTempoParada(velocidadeKmh: number): { minimoS: number; maximoS: number } {
  const v = velocidadeKmh / 3.6
  if (!(v > 0)) return { minimoS: TEMPO_MIN_S, maximoS: TEMPO_MAX_S }
  return {
    minimoS: Math.min(TEMPO_MAX_S, Math.max(TEMPO_MIN_S, 2 * DISTANCIA_MIN_M / v)),
    maximoS: Math.max(TEMPO_MIN_S, Math.min(TEMPO_MAX_S, 2 * DISTANCIA_MAX_M / v)),
  }
}

export function limitarTempoParada(velocidadeKmh: number, tempoS: number): number {
  const { minimoS, maximoS } = limitesTempoParada(velocidadeKmh)
  return Math.min(maximoS, Math.max(minimoS, Number.isFinite(tempoS) ? tempoS : minimoS))
}

export function limitarDistanciaParada(distanciaM: number, maximoM = DISTANCIA_MAX_M): number {
  return Math.min(maximoM, Math.max(DISTANCIA_MIN_M, Number.isFinite(distanciaM) ? distanciaM : DISTANCIA_MIN_M))
}

/**
 * Altura de queda livre (sem ar) que produz a mesma velocidade: h = v²/(2g).
 * Compara a velocidade, não a lesão: a parada depois da queda depende de outra distância.
 */
export function alturaQuedaEquivalenteM(velocidadeKmh: number): number {
  const v = velocidadeKmh / 3.6
  return v * v / (2 * GRAVIDADE_M_S2)
}

/** Energia cinética (J) do PHTLS: Ec = ½ · m · v², com v em m/s. */
export function energiaCineticaJ(massaKg: number, velocidadeKmh: number): number {
  const v = velocidadeKmh / 3.6
  return 0.5 * massaKg * v * v
}

export interface ComparacaoEnergia {
  /** Quantas vezes a energia atual cabe na referência; `null` com referência nula. */
  vezes: number | null
  /** Parte do multiplicador que vem da massa: m / m₀ (linear). */
  fatorMassa: number | null
  /** Parte que vem da velocidade: (v / v₀)² (quadrática). */
  fatorVelocidade: number | null
}

/**
 * Decompõe a razão entre duas energias nos dois termos da fórmula, para o
 * aluno ver que a massa entra uma vez e a velocidade entra ao quadrado.
 */
export function compararEnergia(
  atual: { massaKg: number; velocidadeKmh: number },
  referencia: { massaKg: number; velocidadeKmh: number },
): ComparacaoEnergia {
  const fatorMassa = referencia.massaKg > 0 ? atual.massaKg / referencia.massaKg : null
  const fatorVelocidade = referencia.velocidadeKmh > 0 ? (atual.velocidadeKmh / referencia.velocidadeKmh) ** 2 : null
  return {
    vezes: fatorMassa !== null && fatorVelocidade !== null ? fatorMassa * fatorVelocidade : null,
    fatorMassa,
    fatorVelocidade,
  }
}

/** Força média (N) para uma massa acompanhar a parada: F = m·a. A massa não muda. */
export function forcaMediaN(massaKg: number, desaceleracaoMs2: number): number {
  return massaKg * desaceleracaoMs2
}

/** Quantas vezes a animação está mais lenta que o evento físico. */
export function fatorCamaraLenta(tempoFisicoS: number, duracaoExibidaS: number): number {
  return tempoFisicoS > 0 ? duracaoExibidaS / tempoFisicoS : 0
}

export interface ReferenciaG {
  id: string
  rotulo: string
  g: number
  duracao: string
}

/**
 * Ordens de grandeza aproximadas para dar sentido ao número em g. A duração faz
 * parte da comparação: poucos g por segundos não equivalem a muitos g por milissegundos.
 */
export const REFERENCIAS_G: readonly ReferenciaG[] = [
  { id: 'repouso', rotulo: 'Parado: o próprio peso', g: 1, duracao: 'contínuo' },
  { id: 'frenagem', rotulo: 'Frenagem de emergência em asfalto seco', g: 0.9, duracao: 'alguns segundos' },
  { id: 'montanhaRussa', rotulo: 'Montanha-russa intensa', g: 4, duracao: '1 a 2 segundos' },
  { id: 'cacador', rotulo: 'Piloto de caça em curva, com traje anti-g', g: 9, duracao: 'alguns segundos' },
]
